<script setup lang="ts">
  import {
    Button,
    ButtonAppearance,
    ButtonSize,
    Picture,
    PictureFormat,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { computed, onMounted } from 'vue';
  import { dataLayerTracker, yandexTrackingService } from '@/entities/analytics';
  import { AppDownloadPartner, DeviceType, downloadApp, useDeviceType } from '@/entities/device';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import BannerDecor from './BannerDecor.vue';
  import {
    APP_INSTALL_BANNER_CLICK_VALUE,
    APP_INSTALL_BANNER_NAME,
    APP_INSTALL_BANNER_SHOW_VALUE,
  } from '../config/appDownload';
  import { SERP_APP_INSTALL_BANNER_TEXTS } from '../config/texts';
  import { getAppInstallBannerTitle } from '../lib/appInstallTitle';

  interface AppInstallBannerProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
  }

  const props = defineProps<AppInstallBannerProps>();

  const deviceType = useDeviceType();

  const title = computed(() => getAppInstallBannerTitle(props.searchCriteria));

  const install = (): void => {
    dataLayerTracker.bannerClick(APP_INSTALL_BANNER_CLICK_VALUE);
    yandexTrackingService.bannerThClick(false);
    downloadApp({
      bannerName: APP_INSTALL_BANNER_NAME,
      deviceType: deviceType.value ?? DeviceType.Android,
      partner: AppDownloadPartner.MobileWeb,
      trackDataLayer: false,
    });
  };

  onMounted(() => {
    dataLayerTracker.bannerShow(APP_INSTALL_BANNER_SHOW_VALUE);
  });
</script>

<template>
  <section
    class="app-install-banner"
    data-testid="app-install-banner"
  >
    <Picture
      class="app-install-banner__picture"
      path="illustrations/travel.webp"
      :extensions="[PictureFormat.WEBP]"
      :high-resolution="true"
      fit="contain"
      aria-hidden="true"
      data-testid="app-install-banner-picture"
    />

    <div class="app-install-banner__card">
      <BannerDecor variant="app-install" />

      <div class="app-install-banner__content">
        <div class="app-install-banner__text">
          <Typo
            class="app-install-banner__title"
            :variant="TypoVariant.Heading5"
            tag="p"
          >
            {{ title }}
          </Typo>
          <Typo
            :variant="TypoVariant.BodyMRegular"
            tag="p"
          >
            {{ SERP_APP_INSTALL_BANNER_TEXTS.subtitle }}
          </Typo>
        </div>

        <Button
          class="app-install-banner__install"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.M"
          data-testid="app-install-banner-install"
          @click="install"
        >
          {{ SERP_APP_INSTALL_BANNER_TEXTS.install }}
        </Button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  $picture-size: 323px;
  $picture-top: -56px;
  $card-top: 83px;
  $picture-space: 107px;

  .app-install-banner {
    align-items: center;
    display: flex;
    flex-direction: column;
    isolation: isolate;
    padding-top: var(--spacing-xl);

    &__picture {
      height: $picture-size;
      margin-top: $picture-top;
      pointer-events: none;
      position: relative;
      width: $picture-size;
      z-index: 1;
    }

    &__card {
      align-self: stretch;
      background: linear-gradient(230deg, rgb(244 114 182) 0%, rgb(244 114 182) 11%, rgb(236 72 153) 100%);
      border-radius: var(--rounding-l);
      box-sizing: border-box;
      color: var(--color-text-inverse);
      margin-top: $card-top - $picture-top - $picture-size;
      overflow: hidden;
      padding: calc(#{$picture-space} + var(--spacing-m) * 2) var(--spacing-m) var(--spacing-m);
      position: relative;
    }

    &__content {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      position: relative;
      text-align: center;
    }

    &__text {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    &__title {
      max-width: 255px;
    }

    &__install {
      border: none;
    }
  }
</style>
